/**
 * PDF Block Template Types
 * 
 * A block template is a reusable "mini-template" for displaying a single product.
 * It contains elements with RELATIVE positions within the block's boundaries.
 * When placed on a multi-product template, the block's position is absolute,
 * but elements inside are relative to the block's top-left corner.
 */

import { IPdfElementStyle, IPdfElementBinding, PdfElementKind } from './pdfTemplateTypes';

// Element within a block - uses relative positioning within the block
export interface IPdfBlockElement {
  id: string;
  kind: PdfElementKind;
  xMm: number; // Relative X position within block
  yMm: number; // Relative Y position within block
  wMm: number;
  hMm: number;
  style: IPdfElementStyle;
  binding?: IPdfElementBinding;
  content?: string;
  imageUrl?: string;
  zIndex?: number;
  rotation?: number;
}

// The block template
export interface IPdfBlockTemplate {
  _id?: string;
  type: 'products' | 'wine';
  name: string;
  description?: string;
  wMm: number; // Block width
  hMm: number; // Block height
  elements: IPdfBlockElement[];
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

// Default block template
export const DEFAULT_BLOCK_TEMPLATE: Omit<IPdfBlockTemplate, '_id'> = {
  type: 'products',
  name: '',
  description: '',
  wMm: 90,
  hMm: 120,
  elements: [],
  isActive: true,
};

// Default block dimensions for different use cases
export const BLOCK_SIZE_PRESETS = {
  compact: { wMm: 60, hMm: 80, label: 'Kompakt (60×80 mm)' },
  standard: { wMm: 90, hMm: 120, label: 'Standard (90×120 mm)' },
  large: { wMm: 120, hMm: 160, label: 'Stor (120×160 mm)' },
  wide: { wMm: 180, hMm: 100, label: 'Bred (180×100 mm)' },
};

// Preset block elements for quick start
export const BLOCK_ELEMENT_PRESETS: Record<string, Omit<IPdfBlockElement, 'id'>> = {
  productImage: {
    kind: 'productImage',
    xMm: 5,
    yMm: 5,
    wMm: 80,
    hMm: 60,
    style: { imageAlign: 'center' },
  },
  productName: {
    kind: 'textField',
    xMm: 5,
    yMm: 70,
    wMm: 80,
    hMm: 10,
    style: {
      fontFamily: 'Kapra Neue',
      fontSize: 14,
      fontWeight: 'bold',
      textAlign: 'left',
      autoFit: true,
      minFontSize: 8,
    },
    binding: { metadataKey: 'name', fallbackValue: 'Produktnamn' },
  },
  ean: {
    kind: 'textField',
    xMm: 5,
    yMm: 82,
    wMm: 40,
    hMm: 6,
    style: {
      fontFamily: 'Arial',
      fontSize: 9,
      color: '#666666',
    },
    binding: { metadataKey: 'ean', prefix: 'EAN: ' },
  },
  weight: {
    kind: 'textField',
    xMm: 50,
    yMm: 82,
    wMm: 35,
    hMm: 6,
    style: {
      fontFamily: 'Arial',
      fontSize: 9,
      color: '#666666',
      textAlign: 'right',
    },
    binding: { metadataKey: 'weight' },
  },
  description: {
    kind: 'textField',
    xMm: 5,
    yMm: 90,
    wMm: 80,
    hMm: 25,
    style: {
      fontFamily: 'Arial',
      fontSize: 8,
      lineHeight: 1.3,
      autoFit: true,
      minFontSize: 6,
    },
    binding: { metadataKey: 'description', fallbackValue: 'Produktbeskrivning...' },
  },
};
